import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Download, FolderPlus, FolderTree as FolderTreeIcon, LoaderCircle, MoreHorizontal, Share2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import FolderTree, { folderRowClassName, type FolderTreeNode } from '@/components/doc-control/FolderTree'
import { cn } from '@/lib/utils'
import { CREATE_PHOTO_FOLDER, DELETE_PHOTO_FOLDER, MOVE_PHOTO_FOLDER, PHOTO_FOLDERS, RENAME_PHOTO_FOLDER, UPDATE_PHOTOS } from '../api'
import { describePhotosError, photosErrorKey } from '../lib/errors'
import { ALL_PHOTOS, buildFolderTree, folderBreadcrumb, folderLabel as labelOf, folderLabels, readDraggedPhotoIds, reorderSiblings } from '../lib/folders'
import type { PhotoUi } from '../lib/permissions'
import type { PhotoFolder } from '../types'
import DeleteFolderDialog from './DeleteFolderDialog'
import FolderNameDialog from './FolderNameDialog'
import MoveFolderDialog from './MoveFolderDialog'
import PhotoFolderPanelSkeleton from './PhotoFolderPanelSkeleton'

interface Props {
  projectId: string
  selectedFolderId: string
  onPhotosMoved?: () => void
  onSelect: (id: string) => void
  includeSubfolders: boolean
  onIncludeSubfoldersChange: (v: boolean) => void
  ui: PhotoUi
  onShareFolder?: (folderId: string) => void
}

type Dialog =
  | { kind: 'create'; parentId: string | null }
  | { kind: 'rename'; folder: PhotoFolder }
  | { kind: 'move'; folder: PhotoFolder }
  | { kind: 'delete'; folder: PhotoFolder }
  | null

const REFETCH = ['photos', 'photoFolders']
const MUTATION_OPTS = { context: { handlesErrors: true }, refetchQueries: REFETCH }

const PhotoFolderPanel: React.FC<Props> = ({ projectId, selectedFolderId, onSelect, includeSubfolders, onIncludeSubfoldersChange, ui, onPhotosMoved, onShareFolder }) => {
  const { t } = useTranslation()
  const { data, loading, refetch: refetchFolders } = useQuery<{ photoFolders: PhotoFolder[] }>(PHOTO_FOLDERS, { variables: { projectId } })
  const [dialog, setDialog] = useState<Dialog>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [create] = useMutation(CREATE_PHOTO_FOLDER, MUTATION_OPTS)
  const [rename] = useMutation(RENAME_PHOTO_FOLDER, MUTATION_OPTS)
  const [move] = useMutation(MOVE_PHOTO_FOLDER, MUTATION_OPTS)
  const [remove] = useMutation(DELETE_PHOTO_FOLDER, MUTATION_OPTS)
  const [updatePhotos] = useMutation(UPDATE_PHOTOS, MUTATION_OPTS)

  const folders: PhotoFolder[] = data?.photoFolders ?? []
  const tree = useMemo(() => buildFolderTree(folders, folderLabels(t)), [folders, t])
  const byId = (id: string) => folders.find((x) => x.id === id) as PhotoFolder

  /** Runs a mutation; resolves to the server's message (an i18n key for expected failures) or null. */
  const attempt = async (run: () => Promise<unknown>): Promise<string | null> => {
    try {
      await run()
      return null
    } catch (e) {
      return photosErrorKey(e as Error) || 'error'
    }
  }

  const swapWithNeighbour = async (folder: PhotoFolder, direction: -1 | 1) => {
    const changes = reorderSiblings(folders.filter((x) => x.parentId === folder.parentId && !x.isInbox && !x.system), folder.id, direction)
    if (changes.length === 0) return
    setBusyId(folder.id)
    const failure = await attempt(async () => {
      for (const c of changes) {
        await move({ variables: { id: c.id, parentId: folder.parentId, sortOrder: c.sortOrder } })
      }
    })
    setBusyId(null)
    if (failure) {
      toastr.error(t('Common_error'), describePhotosError(failure, t))
      refetchFolders()
    }
  }

  const dropOnFolder = async (folderId: string, e: React.DragEvent) => {
    const ids = readDraggedPhotoIds(e)
    if (ids.length === 0) return
    setBusyId(folderId)
    const failure = await attempt(() => updatePhotos({ variables: { ids, input: { folderId } } }))
    setBusyId(null)
    if (failure) toastr.error(t('Common_error'), describePhotosError(failure, t))
    else {
      onPhotosMoved?.()
      toastr.success('', t('Photos_moved_to_folder', { count: ids.length, name: folderLabel(folderId) }))
    }
  }

  const folderLabel = (id: string) => {
    const f = byId(id)
    return f ? labelOf(f, folderLabels(t)) : ''
  }

  const renderActions = (node: FolderTreeNode) => {
    const folder = byId(node.id)
    if (!folder) return null
    if (busyId === folder.id) return <LoaderCircle className="h-4 w-4 animate-spin text-muted-foreground" />
    const structure = ui.canManageStructure && !folder.isInbox && !folder.system
    const siblings = folders.filter((x) => x.parentId === folder.parentId && !x.isInbox && !x.system).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
    const index = siblings.findIndex((x) => x.id === folder.id)
    const canDelete = structure && folder.canDelete
    if (!structure && !ui.canExport && !onShareFolder) return null
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title={t('Photos_folder_actions')} aria-label={t('Photos_folder_actions')}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {structure && (
            <>
              <DropdownMenuItem onSelect={() => setDialog({ kind: 'create', parentId: folder.id })}>{t('Photos_new_subfolder')}</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setDialog({ kind: 'rename', folder })}>{t('Photos_rename_folder')}</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setDialog({ kind: 'move', folder })}>{t('Photos_move_folder')}</DropdownMenuItem>
              <DropdownMenuItem disabled={index <= 0} onSelect={() => swapWithNeighbour(folder, -1)}>{t('Photos_move_up')}</DropdownMenuItem>
              <DropdownMenuItem disabled={index < 0 || index >= siblings.length - 1} onSelect={() => swapWithNeighbour(folder, 1)}>{t('Photos_move_down')}</DropdownMenuItem>
            </>
          )}
          {onShareFolder && (
            <DropdownMenuItem onSelect={() => onShareFolder(folder.id)}>
              <Share2 className="h-4 w-4" />
              {t('Photos_share_folder')}
            </DropdownMenuItem>
          )}
          {ui.canExport && (
            <DropdownMenuItem
              onSelect={() => window.open(`/download/photos/${projectId}?folderId=${folder.id}&includeSubfolders=1`)}
            >
              <Download className="h-4 w-4" />
              {t('Photos_download_zip')}
            </DropdownMenuItem>
          )}
          {canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => setDialog({ kind: 'delete', folder })}>
                {t('Photos_delete_folder')}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  if (loading && !data) return <PhotoFolderPanelSkeleton />

  const closeDialog = () => setDialog(null)
  const failureText = (key: string) => describePhotosError(key, t)

  return (
    <div className="space-y-2">
      {ui.canManageStructure && (
        <Button variant="outline" size="sm" className="h-8 w-full" onClick={() => setDialog({ kind: 'create', parentId: null })}>
          <FolderPlus className="h-4 w-4" />
          {t('Photos_new_folder')}
        </Button>
      )}
      <div
        role="button"
        tabIndex={0}
        className={cn(folderRowClassName({ selected: selectedFolderId === ALL_PHOTOS }), 'pl-1')}
        onClick={() => onSelect(ALL_PHOTOS)}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect(ALL_PHOTOS)}
      >
        <FolderTreeIcon className="mx-1.5 h-4 w-4 shrink-0 text-muted-foreground" />
        {t('Photos_all_photos')}
      </div>
      <FolderTree
        folders={tree}
        selectedId={selectedFolderId === ALL_PHOTOS ? null : selectedFolderId}
        onSelect={onSelect}
        onDropOnFolder={ui.canEdit ? dropOnFolder : undefined}
        renderRowActions={renderActions}
        emptyText={t('Photos_no_folders')}
        aria-label={t('Photos_folders')}
      />
      <div className="flex items-center gap-2 pt-2">
        <Switch id="photos-include-subfolders" checked={includeSubfolders} onCheckedChange={onIncludeSubfoldersChange} />
        <Label htmlFor="photos-include-subfolders" className="text-sm font-normal">{t('Photos_include_subfolders')}</Label>
      </div>

      <FolderNameDialog
        open={dialog?.kind === 'create' || dialog?.kind === 'rename'}
        title={dialog?.kind === 'rename' ? t('Photos_rename_folder') : dialog?.kind === 'create' && dialog.parentId ? t('Photos_new_subfolder') : t('Photos_new_folder')}
        initialName={dialog?.kind === 'rename' ? dialog.folder.name : ''}
        onClose={closeDialog}
        onSubmit={async (name) => {
          if (!dialog || (dialog.kind !== 'create' && dialog.kind !== 'rename')) return null
          const failure = await attempt(() =>
            dialog.kind === 'create'
              ? create({ variables: { projectId, parentId: dialog.parentId, name } })
              : rename({ variables: { id: dialog.folder.id, name } }),
          )
          if (failure) return failureText(failure)
          closeDialog()
          return null
        }}
      />
      <MoveFolderDialog
        folder={dialog?.kind === 'move' ? dialog.folder : null}
        folders={folders}
        onClose={closeDialog}
        onSubmit={async (parentId) => {
          if (dialog?.kind !== 'move') return null
          const failure = await attempt(() => move({ variables: { id: dialog.folder.id, parentId } }))
          if (failure) return failureText(failure)
          closeDialog()
          return null
        }}
      />
      <DeleteFolderDialog
        folder={dialog?.kind === 'delete' ? dialog.folder : null}
        folders={folders}
        onClose={closeDialog}
        onSubmit={async (targetFolderId) => {
          if (dialog?.kind !== 'delete') return null
          const id = dialog.folder.id
          const failure = await attempt(() => remove({ variables: { id, targetFolderId } }))
          if (failure) return { key: failure, text: failureText(failure) }
          if (folderBreadcrumb(folders, selectedFolderId).some((x) => x.id === id)) onSelect(ALL_PHOTOS)
          closeDialog()
          return null
        }}
      />
    </div>
  )
}

export default PhotoFolderPanel
